import { ChangeDetectionStrategy, Component, computed, inject } from '@angular/core';

import { AuthState } from '@data/state';
import { ApplicationRoleType } from '@data/enum';
import { Store } from '@ngxs/store';

const MS_PER_DAY = 1000 * 60 * 60 * 24;
const URGENT_THRESHOLD_DAYS = 3;

@Component({
    selector: 'app-trial-banner',
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    template: `
        @if (visible()) {
            <div class="trial-banner" [class.trial-banner--urgent]="urgent()">
                <span class="mdi mdi-timer-sand" aria-hidden="true"></span>
                @if (daysRemaining() === 0) {
                    <span>Your trial ends today.</span>
                } @else if (daysRemaining() === 1) {
                    <span>Your trial ends tomorrow.</span>
                } @else {
                    <span>Your trial ends in {{ daysRemaining() }} days.</span>
                }
            </div>
        }
    `,
})
export class TrialBannerComponent {
    private readonly _store = inject(Store);

    private readonly _roleType = this._store.selectSignal(AuthState.roleType);
    private readonly _isTrial = this._store.selectSignal(AuthState.isTrial);
    private readonly _trialEndDate = this._store.selectSignal(AuthState.trialEndDate);

    readonly daysRemaining = computed(() => {
        const endDate = this._trialEndDate();
        if (!endDate) return null;
        const diffMs = new Date(endDate).getTime() - Date.now();
        return Math.ceil(diffMs / MS_PER_DAY);
    });

    readonly visible = computed(
        () =>
            this._roleType() === ApplicationRoleType.DISTRIBUTOR_SUPPLIER &&
            this._isTrial() &&
            this.daysRemaining() !== null &&
            this.daysRemaining()! >= 0,
    );

    readonly urgent = computed(() => (this.daysRemaining() ?? Infinity) <= URGENT_THRESHOLD_DAYS);
}
